import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { BondLicenseService } from '../../services/bondLicense.service';
import { PERMISSIONS } from '../../constants/permissions';
import { IBondLicense } from '../../interfaces/commercial.interface';
import { formatDate } from '../../utils/dateUtils';
import BondLicenseDialog from './BondLicenseDialog';
import { enumLabel } from '../../utils/garmentsLabels';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
    ACTIVE: 'success', EXPIRED: 'error', CLOSED: 'default', SUSPENDED: 'warning',
};

/** Bond-warehouse license CRUD (T8, brief item 13.1). Mirrors `UdRegisterList`'s shape -- guarded
 * behind `UD_VIEW`/`UD_MANAGE`, matching `BondLicenseController`'s `@authz.can` guards exactly
 * (interface-facts.md: the controller reuses the Bond/UD keys, no dedicated BOND_LICENSE key). */
const BondLicenseList: React.FC = () => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.UD_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<IBondLicense[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [dialogTarget, setDialogTarget] = useState<IBondLicense | 'new' | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            const res = await BondLicenseService.list(institutionId, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load bond licenses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = async (row: IBondLicense) => {
        if (!window.confirm(`Delete bond license "${row.licenseNo}"?`)) return;
        try {
            await BondLicenseService.remove(institutionId, row.id);
            toast.showSuccess('Bond license deleted');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete bond license');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.bondList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Bond Licenses</Typography>
                {canManage && (
                    <Button data-help="commercial.bondList.new" variant="contained" startIcon={<AddIcon />} onClick={() => setDialogTarget('new')}>
                        New Bond License
                    </Button>
                )}
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.bondList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.bondList.col.license">License No</TableCell>
                                <TableCell data-help="commercial.bondList.col.type">Bond Type</TableCell>
                                <TableCell data-help="commercial.bondList.col.issue">Issue Date</TableCell>
                                <TableCell data-help="commercial.bondList.col.validTill">Valid Till</TableCell>
                                <TableCell data-help="commercial.bondList.col.status">Status</TableCell>
                                {canManage && <TableCell align="right" data-help="commercial.bondList.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.id} hover data-testid={`bond-license-row-${r.id}`}>
                                    <TableCell>{r.licenseNo}</TableCell>
                                    <TableCell>{r.bondType ?? '—'}</TableCell>
                                    <TableCell>{r.issueDate ? formatDate(r.issueDate) : '—'}</TableCell>
                                    <TableCell>{r.validTill ? formatDate(r.validTill) : '—'}</TableCell>
                                    <TableCell><Chip data-help="commercial.bondList.status" size="small" label={enumLabel(r.status)} color={STATUS_COLOR[r.status] ?? 'default'} /></TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <IconButton size="small" aria-label="edit" data-help="commercial.bondList.edit" onClick={() => setDialogTarget(r)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" aria-label="delete" data-help="commercial.bondList.delete" onClick={() => handleDelete(r)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 6 : 5} align="center">No bond licenses found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}

            {dialogTarget && (
                <BondLicenseDialog
                    institutionId={institutionId}
                    target={dialogTarget === 'new' ? null : dialogTarget}
                    onClose={() => setDialogTarget(null)}
                    onSaved={() => { setDialogTarget(null); load(); }}
                />
            )}
        </Box>
    );
};

export default BondLicenseList;
